import React, { useEffect } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Shield, Lock } from 'lucide-react';
import { useFormContext, Controller } from 'react-hook-form';
import { ContractScopeSelector } from '@/components/users/contract-scope-selector';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

export function ContractScope() {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { control, watch, setValue, formState } = useFormContext();

  const contractScope = watch('contractScope');
  const propertyTypeId = watch('property_type_id');
  const selectedDepartmentTouchpoints =
    watch('selectedDepartmentTouchpoints') || [];

  const handleScopeChange = (
    scope: { departmentId: string; touchpointIds: string[] }[]
  ) => {
    setValue('selectedDepartmentTouchpoints', scope, { shouldValidate: true });
  };

  // Initialize contractScope if not set
  useEffect(() => {
    if (!contractScope) {
      const propertyScope = watch('property_scope');
      if (propertyScope && propertyScope.length > 0) {
        setValue('contractScope', 'limited');
      } else {
        setValue('contractScope', 'full');
      }
    }
  }, [contractScope, setValue, watch]);

  return (
    <Card>
      <CardHeader>
        <CardTitle
          className={cn(
            'flex items-center gap-2',
            isRTL ? 'flex-row-reverse' : 'flex-row'
          )}
        >
          <Shield className="w-5 h-5" />
          Contract Scope Configuration
        </CardTitle>
        <CardDescription className={isRTL ? 'text-right' : 'text-left'}>
          Set access scope for this property
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4 p-4 bg-muted/30 rounded-lg border">
          <h3
            className={cn(
              'font-medium text-sm flex items-center gap-2',
              isRTL ? 'flex-row-reverse' : 'flex-row'
            )}
          >
            <Lock className="w-4 h-4" />
            Access Scope
          </h3>

          <div className="space-y-2">
            <Label htmlFor="contractScope">Contract Scope</Label>
            <Controller
              control={control}
              name="contractScope"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select access scope" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full">
                      <div className="space-y-1">
                        <div className="font-medium">Full Access</div>
                        <div className="text-xs text-muted-foreground">
                          Access to all departments and touchpoints
                        </div>
                      </div>
                    </SelectItem>
                    <SelectItem value="limited">
                      <div className="space-y-1">
                        <div className="font-medium">Limited Access</div>
                        <div className="text-xs text-muted-foreground">
                          Access to specific departments and touchpoints only
                        </div>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            {formState.errors.contractScope && (
              <p className="text-sm text-destructive">
                {String(
                  formState.errors.contractScope?.message || 'Invalid selection'
                )}
              </p>
            )}
          </div>

          <div className="text-sm text-muted-foreground">
            {contractScope === 'full' && (
              <p>✓ Property will have access to all assessment areas</p>
            )}
            {contractScope === 'limited' && (
              <p>
                ⚠ You need to select specific departments and touchpoints below
              </p>
            )}
          </div>
        </div>

        {contractScope === 'limited' && propertyTypeId && (
          <ContractScopeSelector
            propertyTypeId={propertyTypeId}
            selectedScope={selectedDepartmentTouchpoints}
            onScopeChange={handleScopeChange}
          />
        )}

        {formState.errors.selectedDepartmentTouchpoints && (
          <p className="text-sm text-destructive">
            {String(
              formState.errors.selectedDepartmentTouchpoints?.message ||
                'Invalid selection'
            )}
          </p>
        )}

        <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <p className="text-sm text-blue-800 dark:text-blue-200">
            <strong>Note:</strong> The contract scope determines which
            departments and touchpoints are accessible for assessments. These
            settings can be modified at any time.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
